/**
 * Navigation Configuration for Condomínio Customers
 * Specific navigation menu for residential condominiums
 */

import { NavGroup } from './types';
import { RoleName } from '../roles';
import { CondoType } from '../../types/customer/condominio';
import { getBlockLabel, getUnitLabel } from '../../utils/terminology';

/**
 * Get navigation configuration for condominio based on condo type
 * @param condoType - Type of condo (casa, apartamento, misto)
 * @returns Navigation configuration with dynamic terminology
 */
export function getCondominioNavigation(condoType?: CondoType | null, customer?: any): NavGroup[] {
  // Get dynamic labels based on condo type
  const blocksLabel = getBlockLabel(condoType, true);
  const unitsLabel = getUnitLabel(condoType, true);
  const timeZonesEnabled = !!(customer?.condo_data?.time_zones_enabled);
  const guaritaEnabled = !!(customer?.condo_data?.guarita_integration_enabled);
  const alphadigiEnabled = !!(customer?.condo_data?.alphadigi_lpr_enabled);
  // Hikvision LPR (DS-TCG406) is configured on the same Integrações page, so enabling it
  // alone must reveal the menu — previously only guarita/alphadigi did, which made the
  // 'Câmera LPR Hikvision' toggle look broken: it saved but the page stayed unreachable.
  const hikvisionLprEnabled = !!(customer?.condo_data?.hikvision_lpr_enabled);
  const commonAreasEnabled = !!(customer?.condo_data?.common_areas_enabled);
  const obrasEnabled = !!(customer?.condo_data?.obras_enabled);
  const keysEnabled = !!(customer?.condo_data?.keys_enabled);
  const petsEnabled = !!(customer?.condo_data?.pets_enabled);
  const normasInternasEnabled = !!(customer?.condo_data?.normas_internas_enabled);
  const ocorrenciasEnabled = !!(customer?.condo_data?.ocorrencias_enabled);
  const muralAnunciosEnabled = !!(customer?.condo_data?.mural_anuncios_enabled);
  const livroEnabled = !!(customer?.condo_data?.livro_enabled);
  const lostFoundEnabled = !!(customer?.condo_data?.lost_found_enabled);
  const funcionariosEnabled = !!(customer?.condo_data?.funcionarios_enabled);
  // Defaults ON, unlike every sibling flag: Solicitações predates the setting (migration 229
  // backfills `true`), and both apps read an absent key as enabled. A customer record that has
  // not been re-saved since must not lose the queue.
  const solicitacoesEnabled = customer?.condo_data?.solicitacoes_enabled !== false;
  return [
    {
      group: 'Visão Geral',
      icon: 'LayoutDashboard',
      minRoleLevel: 70,
      items: [
        { name: 'Condomínio', href: '/dashboard', icon: 'Home' },
      ],
    },
    {
      group: 'Minha Unidade',
      icon: 'Home',
      items: [
        {
          name: 'Meus Visitantes',
          href: '/visitors',
          icon: 'UserCheck',
        },
      ],
      maxRoleLevel: 50, // Only visible to moradores (level 40), not staff (60+) or admins (90)
    },
    {
      group: 'Cadastro',
      icon: 'ClipboardList',
      minRoleLevel: 60, // Staff and above
      items: [
        {
          name: 'Moradores',
          href: '/residents',
          icon: 'BadgeCheck',
        },
        ...(funcionariosEnabled ? [{
          name: 'Funcionários',
          href: '/condo-employees',
          icon: 'Briefcase',
        }] : []),
        {
          name: 'Visitantes',
          href: '/visitors',
          icon: 'UserPlus',
        },
        {
          name: 'Prestadores',
          href: '/prestadores',
          icon: 'Briefcase',
          minRoleLevel: 60, // Staff and above
        },
        {
          // Every clearance in the condomínio — who may come in, to which unit, until when.
          // Previously answerable only inside the Operational View or one unit at a time.
          name: 'Liberados',
          href: '/liberados',
          icon: 'CalendarCheck',
          minRoleLevel: 60, // Staff and above: it is the whole condomínio's visiting schedule
        },
        ...(solicitacoesEnabled ? [{
          // The síndico's side of the residents' credential requests (tag, controle, biometria,
          // troca de foto …). It was only reachable inside the Operational View, so the normal
          // app had no way into the queue the apps keep filling.
          name: 'Solicitações',
          href: '/solicitacoes',
          icon: 'ClipboardCheck',
          minRoleLevel: 60, // Staff and above: a morador files these from the app
        }] : []),
        {
          name: 'Pessoas',
          href: '/people',
          icon: 'Contact',
        },
        {
          name: 'Qualidade de Fotos',
          href: '/photo-quality',
          icon: 'ScanFace',
          minRoleLevel: 60,
          electronOnly: true,
        },
        {
          name: 'Veículos',
          href: '/vehicles',
          icon: 'Car',
        },
        {
          name: 'Encomendas',
          href: '/encomendas',
          icon: 'Package',
        },
        {
          name: 'Tags de Acesso',
          href: '/tags',
          icon: 'Tag',
          minRoleLevel: 60, // Staff and above
        },
        ...(petsEnabled ? [{
          name: 'Pets',
          href: '/pets',
          icon: 'PawPrint',
        }] : []),
      ],
    },
    {
      group: 'Estrutura',
      icon: 'Building2',
      minRoleLevel: 60, // Staff and above
      items: [
        {
          name: blocksLabel,
          href: '/blocks',
          icon: 'SquareStack',
        },
        {
          name: unitsLabel,
          href: '/units',
          icon: 'LayoutGrid',
        },
        ...(commonAreasEnabled ? [{
          name: 'Áreas Comuns',
          href: '/common-areas',
          icon: 'MapPin',
        }] : []),
        ...(obrasEnabled ? [{
          name: 'Obras',
          href: '/obras',
          icon: 'HardHat',
        }] : []),
        ...(keysEnabled ? [{
          name: 'Chaves',
          href: '/keys',
          icon: 'KeyRound',
          minRoleLevel: 60, // Staff (porteiro) and above
        }] : []),
      ],
    },
    ...(commonAreasEnabled ? [{
      group: 'Reservas',
      icon: 'CalendarDays',
      // No minRoleLevel — visible to all authenticated users (moradores included)
      items: [
        {
          name: 'Agendamentos',
          href: '/common-area-reservations',
          icon: 'CalendarCheck',
        },
      ],
    }] : []),
    ...((normasInternasEnabled || ocorrenciasEnabled || muralAnunciosEnabled || livroEnabled || lostFoundEnabled) ? [{
      group: 'Condomínio',
      icon: 'ScrollText',
      items: [
        ...(normasInternasEnabled ? [{
          name: 'Normas Internas',
          href: '/normas-internas',
          icon: 'ScrollText',
        }] : []),
        ...(ocorrenciasEnabled ? [{
          name: 'Ocorrências',
          href: '/ocorrencias',
          icon: 'AlertTriangle',
        }] : []),
        ...(muralAnunciosEnabled ? [{
          name: 'Mural de Anúncios',
          href: '/mural-anuncios',
          icon: 'Megaphone',
        }] : []),
        // Same livro_enabled flag the Operational View's button already honours, so the feature
        // appears and disappears together in both places.
        ...(livroEnabled ? [{
          name: 'Livro de Ocorrências',
          href: '/livro',
          icon: 'BookOpen',
        }] : []),
        // Same lost_found_enabled flag the apps' tiles honour, so the feature appears and
        // disappears together everywhere.
        ...(lostFoundEnabled ? [{
          name: 'Achados e Perdidos',
          href: '/achados-perdidos',
          icon: 'Package',
        }] : []),
      ],
    }] : []),
    {
      group: 'Acesso',
      icon: 'Shield',
      minRoleLevel: 60, // Staff and above
      items: [
        {
          name: 'Monitoramento',
          href: '/access-control',
          icon: 'Monitor',
          // No minRoleLevel - visible to all staff/managers (administrador 65, sindico 70, porteiro 60, etc.)
        },
        {
          name: 'Dispositivos de Pessoas',
          href: '/person-devices',
          icon: 'CreditCard',
          // No minRoleLevel - same audience as Monitoramento
        },
        {
          name: 'Eventos Gerais',
          href: '/eventos-gerais',
          icon: 'Activity',
          excludeRoles: ['sindico'],
        },
        {
          name: 'Zonas & Dispositivos',
          href: '/zones-devices',
          icon: 'Radar',
          minRoleLevel: 70, // Sindico (70) and above - not visible to administrador (65) or porteiro (60)
        },
        {
          name: 'Grupos de Zonas',
          href: '/zone-groups',
          icon: 'Boxes',
          minRoleLevel: 80, // Admin only - hidden from sindico
        },
        {
          name: 'Perfis de Acesso',
          href: '/access-profiles',
          icon: 'Key',
          minRoleLevel: 80, // Admin only - hidden from sindico
        },
        ...(timeZonesEnabled ? [{
          name: 'Zonas de Tempo',
          href: '/time-zones',
          icon: 'Clock',
          minRoleLevel: 80, // Admin only
        }] : []),
        {
          name: 'Câmeras de Cadastro',
          href: '/registration-cameras',
          icon: 'Camera',
          minRoleLevel: 80, // Admin only - hidden from sindico
        },
        {
          name: 'Eventos de Dispositivos',
          href: '/device-audit',
          icon: 'Activity',
          minRoleLevel: 70, // Síndico and above
        },
        {
          name: 'Reconciliação de Acesso',
          href: '/reconciliation',
          icon: 'AlertTriangle',
          minRoleLevel: 70, // Síndico and above — matches backend's manual-resolve role gate
        },
        {
          name: 'Auditoria de Cadastros',
          href: '/role-audit',
          icon: 'ClipboardCheck',
          minRoleLevel: 90, // Admin and super admin only
          excludeRoles: ['sindico'],
        },
      ],
    },
    // Hidden temporarily
    // {
    //   group: 'Gestão',
    //   icon: 'Settings',
    //   items: [
    //     {
    //       name: 'Reservas',
    //       href: '/reservations',
    //       icon: 'Calendar',
    //     },
    //     {
    //       name: 'Correspondências',
    //       href: '/mail',
    //       icon: 'Mail',
    //     },
    //     {
    //       name: 'Manutenção',
    //       href: '/maintenance',
    //       icon: 'Wrench',
    //     },
    //     {
    //       name: 'Ocorrências',
    //       href: '/incidents',
    //       icon: 'AlertTriangle',
    //     },
    //   ],
    //   minRoleLevel: 60, // Staff and above
    // },
    // {
    //   group: 'Financeiro',
    //   icon: 'DollarSign',
    //   items: [
    //     {
    //       name: 'Taxas Condominiais',
    //       href: '/fees',
    //       icon: 'CreditCard',
    //     },
    //     {
    //       name: 'Pagamentos',
    //       href: '/payments',
    //       icon: 'Receipt',
    //     },
    //     {
    //       name: 'Inadimplência',
    //       href: '/overdue',
    //       icon: 'AlertCircle',
    //     },
    //   ],
    //   minRoleLevel: 70, // Manager and above
    // },
    // {
    //   group: 'Comunicação',
    //   icon: 'MessageSquare',
    //   items: [
    //     {
    //       name: 'Avisos',
    //       href: '/announcements',
    //       icon: 'Megaphone',
    //     },
    //     {
    //       name: 'Assembleias',
    //       href: '/meetings',
    //       icon: 'Users',
    //     },
    //     {
    //       name: 'Enquetes',
    //       href: '/polls',
    //       icon: 'BarChart3',
    //     },
    //   ],
    //   minRoleLevel: 70, // Manager and above
    // },
    {
      group: 'Avançado',
      icon: 'Sliders',
      items: [
        {
          name: 'Gerenciar QR Codes',
          href: '/qr-codes',
          icon: 'QrCode',
          requiredRoles: ['super_admin', 'admin'], // Admin only
        },
        {
          name: 'Importar Dados',
          href: '/import-data',
          icon: 'Upload',
          excludeRoles: ['sindico'],
        },
        {
          name: 'Exportar Dados',
          href: '/export-data',
          icon: 'Download',
          excludeRoles: ['sindico'],
        },
        {
          name: 'Pessoas Não Autorizadas',
          href: '/admin/unauthorized-persons',
          icon: 'AlertTriangle',
          requiredRoles: ['super_admin', 'admin'], // Admin only diagnostic tool
          excludeRoles: ['sindico'],
        },
        ...((guaritaEnabled || alphadigiEnabled || hikvisionLprEnabled) ? [{
          name: 'Integrações',
          href: '/integrations',
          icon: 'Plug',
          requiredRoles: ['super_admin', 'admin'] as RoleName[],
        }] : []),

      ],
      minRoleLevel: 70, // Manager (Síndico) and above
    },
    {
      group: 'Administração',
      icon: 'ShieldCheck',
      items: [
        {
          name: 'Usuários',
          href: '/customer-admin',
          icon: 'Cog',
        },
        {
          name: 'Configurações do Cliente',
          href: '/customer-settings',
          icon: 'Building2',
          requiredRoles: ['super_admin', 'admin'], // Only super_admin and admin
        },
        {
          name: 'Dispositivos Conectados',
          href: '/connected-devices',
          icon: 'Wifi',
          requiredRoles: ['super_admin', 'admin'],
        },
      ],
      minRoleLevel: 70, // Manager (Síndico) and above
    },
    {
      group: 'Ajuda',
      icon: 'HelpCircle',
      items: [
        {
          name: 'Central de Ajuda',
          href: '/support',
          icon: 'Book',
        },
        {
          name: 'Downloads',
          href: '/downloads',
          icon: 'Download',
          minRoleLevel: 70, // Síndico and above
        },
      ],
    },
  ];
}

// Backward compatibility: Default navigation for condominiums (apartamento)
export const CONDOMINIO_NAVIGATION: NavGroup[] = getCondominioNavigation('apartamento', null);
